import { useSearchParams } from 'react-router-dom';
import { filtersToQuery, isQueryValid, queryToFilters } from './aql-functions';

export const AqlBuilderOperator = {
  '=': '=',
  '!=': '!=',
  contains: 'contains',
  not_contains: 'not_contains',
  starts_with: 'starts_with',
  ends_with: 'ends_with',
  in: 'in',
  not_in: 'not_in',
} as const;

export const AqlBuilderOperatorShortDisplayName: Record<AqlBuilderOperator, string> = {
  '=': '=',
  '!=': '!=',
  contains: 'a*b',
  not_contains: '!a*b',
  starts_with: '*b',
  ends_with: 'a*',
  in: 'in',
  not_in: 'not in',
} as const;

export const AqlBuilderOperatorDisplayName: Record<AqlBuilderOperator, string> = {
  '=': `Equals (${AqlBuilderOperatorShortDisplayName['=']})`,
  '!=': `Not Equals (${AqlBuilderOperatorShortDisplayName['!=']})`,
  contains: `Contains (${AqlBuilderOperatorShortDisplayName.contains})`,
  not_contains: `Does not contain (${AqlBuilderOperatorShortDisplayName.not_contains})`,
  in: `In (${AqlBuilderOperatorShortDisplayName.in})`,
  not_in: `Not In (${AqlBuilderOperatorShortDisplayName.not_in})`,
  starts_with: `Starts with (${AqlBuilderOperatorShortDisplayName.starts_with})`,
  ends_with: `Ends with (${AqlBuilderOperatorShortDisplayName.ends_with})`,
} as const;

export function isFreeTextOperator(operator: AqlBuilderOperator): boolean {
  switch (operator) {
    case AqlBuilderOperator.contains:
    case AqlBuilderOperator.not_contains:
    case AqlBuilderOperator.starts_with:
    case AqlBuilderOperator.ends_with:
      return true;
    default:
      return false;
  }
}

export interface AqlFilterItem {
  field: AqlBuilderField;
  andExpression?: AqlFieldExpression;
  orExpressions?: AqlFieldExpression[];
}

export interface AqlFieldExpression {
  key?: string;
  operator: AqlBuilderOperator;
  value: string[];
}

export const AqlBuilderField = {
  integration: 'integration',
  // integration_name: 'integration_name',
  resource_type: 'resource_type',
  resource_name: 'resource_name',
  resource_tag: 'resource_tag',
  resource_status: 'resource_status',
  permission_name: 'permission_name',
  permission_tag: 'permission_tag',
  resource_risk_level: 'resource_risk_level',
  permission_risk_level: 'permission_risk_level',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type AqlBuilderField = (typeof AqlBuilderField)[keyof typeof AqlBuilderField];

export const AqlBuilderFieldName: Record<AqlBuilderField, string> = {
  integration: 'Integration',
  // integration_name: 'Integration Name',
  resource_name: 'Resource Name',
  resource_type: 'Resource Type',
  resource_tag: 'Resource Tag',
  resource_status: 'Resource Status',
  permission_name: 'Permission Name',
  permission_tag: 'Permission Tag',
  resource_risk_level: 'Resource Risk Level',
  permission_risk_level: 'Permission Risk Level',
};

export const AqlBuilderFieldOperators: Record<AqlBuilderField, AqlBuilderOperator[]> = {
  resource_type: ['=', '!='],
  integration: ['=', '!='],
  resource_status: ['=', '!='],
  // integration_name: ['=', '!=', 'contains', 'not_contains', 'starts_with', 'ends_with'],
  resource_name: ['=', '!=', 'contains', 'not_contains', 'starts_with', 'ends_with'],
  permission_name: ['=', '!=', 'contains', 'not_contains', 'starts_with', 'ends_with'],
  resource_tag: ['=', '!=', 'contains', 'not_contains', 'starts_with', 'ends_with'],
  permission_tag: ['=', '!=', 'contains', 'not_contains', 'starts_with', 'ends_with'],
  resource_risk_level: ['=', '!='],
  permission_risk_level: ['=', '!='],
};

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type AqlBuilderOperatorDisplayName =
  (typeof AqlBuilderOperatorDisplayName)[keyof typeof AqlBuilderOperatorDisplayName];
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type AqlBuilderOperatorShortDisplayName =
  (typeof AqlBuilderOperatorShortDisplayName)[keyof typeof AqlBuilderOperatorShortDisplayName];
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type AqlBuilderOperator = (typeof AqlBuilderOperator)[keyof typeof AqlBuilderOperator];

export const QUERY_QP = 'query';

export const isAqlBuilderField = (field: string): field is AqlBuilderField => {
  return Object.values(AqlBuilderField).includes(field as AqlBuilderField);
};

export const isAqlBuilderOperator = (operator: string): operator is AqlBuilderOperator => {
  return Object.values(AqlBuilderOperator).includes(operator as AqlBuilderOperator);
};

export const isIdField = (field: AqlBuilderField): boolean => {
  switch (field) {
    case AqlBuilderField.integration:
    case AqlBuilderField.resource_type:
      return true;
    default:
      return false;
  }
};

export const isFieldHasKey = (field: AqlBuilderField): boolean => {
  switch (field) {
    case AqlBuilderField.resource_tag:
    case AqlBuilderField.permission_tag:
      return true;
    default:
      return false;
  }
};

export function useGetQueryQP(excludeFields?: AqlBuilderField[]) {
  const [searchParams] = useSearchParams();
  const query = searchParams.get(QUERY_QP);

  if (!query || !isQueryValid(query)) {
    return '';
  }

  if (!excludeFields || excludeFields.length === 0) {
    return query;
  }

  const filters = queryToFilters(query);
  const newFilters = filters.filter((f) => !excludeFields.includes(f.field));
  return filtersToQuery(newFilters);
}
